import React, { useState, useEffect } from "react";
import {
  Flex,
  Text,
  Table,
  Tbody,
  Tr,
  Td,
  Box,
  Button,
  Spinner,
  Input,
  Select,
  Stack,
  FormControl,
  FormLabel,
  Switch,
  useToast,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import fetchWithToken from "views/auth/signIn/axiosInstance";

export const Setting = () => {
  // Token Settings state
  const [settings, setSettings] = useState(null);
  const [editableSettings, setEditableSettings] = useState({ daily_token_limit: 0, last_updated: "" });
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);

  // Feedback delete state
  const [fbFilters, setFbFilters] = useState({
    time_filter_type: "",
    year: "",
    month: "",
    start_date: "",
    end_date: "",
    date: "",
    sentiment: "",
    priority: "",
    user_id: "",
    feedback_ids: "",
  });
  const [loadingFbDelete, setLoadingFbDelete] = useState(false);

  // Chat delete state
  const [chatFilters, setChatFilters] = useState({
    time_filter_type: "",
    year: "",
    month: "",
    start_date: "",
    end_date: "",
    date: "",
    user_ids: "",
    chat_ids: "",
  });
  const [loadingChatDelete, setLoadingChatDelete] = useState(false);

  // Feedback export state
  const [exportFilters, setExportFilters] = useState({
    time_filter_type: "",
    year: "",
    month: "",
    start_date: "",
    end_date: "",
    date: "",
    sentiment: "",
    priority: "",
    user_id: "",
  });
  const [loadingExport, setLoadingExport] = useState(false);

  // Chat export state
  const [chatExportFilters, setChatExportFilters] = useState({
    time_filter_type: "",
    year: "",
    month: "",
    start_date: "",
    end_date: "",
    date: "",
    user_ids: "",
    include_messages: false,
  });
  const [loadingChatExport, setLoadingChatExport] = useState(false);

  const toast = useToast();

  // Load token settings
  const fetchSettings = async () => {
    try {
      setLoadingSettings(true);
      const response = await fetchWithToken(`${process.env.REACT_APP_API_URL3}/tokens/settings`);
      if (!response) throw new Error("Failed to fetch token settings");
      setSettings(response);
      setEditableSettings(response);
      setIsEditMode(false);
    } catch (err) {
      console.error(err);
      toast({ title: "Error", description: "Failed to load token settings", status: "error", duration: 5000, isClosable: true });
    } finally {
      setLoadingSettings(false);
    }
  };

  useEffect(() => { fetchSettings(); }, []);

  // Token settings handlers
  const handleSettingsChange = (field, value) => setEditableSettings(prev => ({ ...prev, [field]: value }));
  const handleSettingsSave = async () => {
    try {
      setLoadingSettings(true);
      const payload = { daily_token_limit: Number(editableSettings.daily_token_limit), last_updated: new Date().toISOString() };
      await fetchWithToken(`${process.env.REACT_APP_API_URL3}/tokens/settings`, "PUT", payload);
      toast({ title: "Success", description: "Token settings updated", status: "success", duration: 5000, isClosable: true });
      fetchSettings();
    } catch {
      toast({ title: "Error", description: "Failed to update token settings", status: "error", duration: 5000, isClosable: true });
      setLoadingSettings(false);
    }
  };
  const handleSettingsCancel = () => { setEditableSettings(settings); setIsEditMode(false); };

  // Feedback delete handlers
  const handleFbChange = (field) => (e) => setFbFilters(prev => ({ ...prev, [field]: e.target.value }));
  const handleFbDelete = async () => {
    setLoadingFbDelete(true);
    try {
      const payload = {};
      if (fbFilters.feedback_ids) payload.feedback_ids = fbFilters.feedback_ids.split(",").map(i => i.trim()).filter(Boolean);
      else ["time_filter_type","year","month","start_date","end_date","date","sentiment","priority","user_id"].forEach(k => fbFilters[k] && (payload[k] = fbFilters[k]));
      await fetchWithToken(`${process.env.REACT_APP_API_URL3}/feedbacks/delete-batch`, "DELETE", payload);
      toast({ title: "Deleted", description: "Feedbacks deleted successfully", status: "success", duration: 5000, isClosable: true });
      setFbFilters({ time_filter_type: "", year: "", month: "", start_date: "", end_date: "", date: "", sentiment: "", priority: "", user_id: "", feedback_ids: "" });
    } catch (err) {
      toast({ title: "Error", description: err.message || "Failed to delete feedbacks", status: "error", duration: 5000, isClosable: true });
    } finally { setLoadingFbDelete(false); }
  };

  // Chat delete handlers
  const handleChatChange = (field) => (e) => setChatFilters(prev => ({ ...prev, [field]: e.target.value }));
  const handleChatDelete = async () => {
    setLoadingChatDelete(true);
    try {
      const payload = {};
      if (chatFilters.chat_ids) payload.chat_ids = chatFilters.chat_ids.split(",").map(i => i.trim()).filter(Boolean);
      else ["time_filter_type","year","month","start_date","end_date","date","user_ids"].forEach(k => chatFilters[k] && (payload[k] = chatFilters[k]));
      await fetchWithToken(`${process.env.REACT_APP_API_URL3}/chats/delete-batch`, "DELETE", payload);
      toast({ title: "Deleted", description: "Chats deleted successfully", status: "success", duration: 5000, isClosable: true });
      setChatFilters({ time_filter_type: "", year: "", month: "", start_date: "", end_date: "", date: "", user_ids: "", chat_ids: "" });
    } catch (err) {
      toast({ title: "Error", description: err.message || "Failed to delete chats", status: "error", duration: 5000, isClosable: true });
    } finally { setLoadingChatDelete(false); }
  };

  // Feedback export handlers
  const handleExportChange = (field) => (e) => setExportFilters(prev => ({ ...prev, [field]: e.target.value }));
  const handleExport = async () => {
    setLoadingExport(true);
    try {
      // 1) Build the payload
      const rawPayload = {
        time_filter_type: exportFilters.time_filter_type,
        year: Number(exportFilters.year) || 0,
        month: Number(exportFilters.month) || 0,
        start_date: exportFilters.start_date,
        end_date: exportFilters.end_date,
        date_: exportFilters.date, // note the underscore
        sentiment: exportFilters.sentiment,
        priority: exportFilters.priority,
        user_id: exportFilters.user_id,
      };
  
      // 2) Remove undefined, null, or empty string fields
      const payload = {};
      Object.keys(rawPayload).forEach((key) => {
        const value = rawPayload[key];
        if (value !== undefined && value !== null && value !== "") {
          payload[key] = value;
        }
      });
  
      // 3) Fire a raw fetch so we get headers + blob
      const response = await fetch(`${process.env.REACT_APP_API_URL3}/analytics/feedbacks/export`, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          // Add your auth header if needed
        },
        body: JSON.stringify(payload),
      });
  
      if (!response.ok) {
        throw new Error(`Export failed (${response.status})`);
      }
  
      // 4) Read it as a Blob
      const blob = await response.blob();
  
      // 5) Pull filename from Content-Disposition
      const dispo = response.headers.get("content-disposition") || "";
      const match = dispo.match(/filename="?(.+?)"?(\s*;|$)/);
      const filename = match ? match[1] : "feedback_export.csv";
  
      // 6) Trigger browser download
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
  
      toast({
        title: "Exported",
        description: `${filename} downloaded`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error(err);
      toast({
        title: "Error",
        description: err.message || "Failed to export feedbacks",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingExport(false);
    }
  };
  
  

  // Chat export handlers
  const handleChatExportChange = (field) => (e) => {
    const value = field === "include_messages" ? e.target.checked : e.target.value;
    setChatExportFilters(prev => ({ ...prev, [field]: value }));
  };
  const handleChatExport = async () => {
    setLoadingChatExport(true);
    try {
      // 1) Build payload exactly as the API expects
      const payload = {
        time_filter_type: chatExportFilters.time_filter_type,
        year: Number(chatExportFilters.year) || 0,
        month: Number(chatExportFilters.month) || 0,
        start_date: chatExportFilters.start_date || undefined,
        end_date: chatExportFilters.end_date || undefined,
        date_: chatExportFilters.date_ || undefined,       // note the underscore
        user_ids: chatExportFilters.user_ids
          ? chatExportFilters.user_ids
              .split(",")
              .map((id) => id.trim())
              .filter(Boolean)
          : [],
        include_messages: chatExportFilters.include_messages,
      };
  
      // 2) Fire off a raw fetch so we get the Response object
      const response = await fetch(`${process.env.REACT_APP_API_URL3}/analytics/chats/export`, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          // include your auth header here if needed, e.g.
          // "Authorization": `Bearer ${yourToken}`
        },
        body: JSON.stringify(payload),
      });
  
      if (!response.ok) {
        throw new Error(`Export failed: ${response.status}`);
      }
  
      // 3) Read the CSV as a Blob
      const blob = await response.blob();
  
      // 4) Pull the filename out of the Content-Disposition header
      const disposition = response.headers.get("content-disposition") || "";
      const match = disposition.match(/filename="?(.+?)"?(\s*;|$)/);
      const filename = match ? match[1] : "chat_export.csv";
  
      // 5) Create a download link and click it
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
  
      toast({
        title: "Exported",
        description: `${filename} downloaded`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error(err);
      toast({
        title: "Error",
        description: err.message || "Failed to export chats",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingChatExport(false);
    }
  };
  

  // Loading state
  if (loadingSettings) return <Flex justify="center" align="center" h="100%"><Spinner size="lg" /></Flex>;
  if (!settings) return <Text color="red.500">Unable to load token settings.</Text>;

  return (
    <Box>
      {/* Token Settings */}
      <Card mb={8}>
        <Flex px={6} py={4} justify="space-between" align="center">
          <Text fontSize="xl" fontWeight="bold">Token Settings</Text>
          <Flex>
            {!isEditMode ? (
              <>
                <Button size="sm" onClick={fetchSettings} mr={2}>Refresh</Button>
                <Button size="sm" colorScheme="blue" onClick={() => setIsEditMode(true)}>Edit</Button>
              </>
            ) : (
              <>
                <Button size="sm" colorScheme="red" onClick={handleSettingsCancel} mr={2}>Cancel</Button>
                <Button size="sm" colorScheme="green" onClick={handleSettingsSave}>Save</Button>
              </>
            )}
          </Flex>
        </Flex>
        <Box px={6} pb={4}>
          <Table variant="simple">
            <Tbody>
              <Tr><Td fontWeight="600">Daily Token Limit</Td><Td><Input size="sm" type="number" value={editableSettings.daily_token_limit} isDisabled={!isEditMode} onChange={e => handleSettingsChange("daily_token_limit", e.target.value)} /></Td></Tr>
              <Tr><Td fontWeight="600">Last Updated</Td><Td>{new Date(settings.last_updated).toLocaleString()}</Td></Tr>
            </Tbody>
          </Table>
        </Box>
      </Card>

      {/* Feedback Export */}
      <Card mb={8}>
        <Box px={6} py={4}>
          <Text fontSize="xl" fontWeight="bold" mb={4}>Export Feedback Data</Text>
          <Stack spacing={4}>
            <FormControl>
              <FormLabel>Time Filter Type</FormLabel>
              <Select placeholder="Select" value={exportFilters.time_filter_type} onChange={handleExportChange("time_filter_type")}>                
                <option value="last_day">Last Day</option>
                <option value="last_week">Last Week</option>
                <option value="last_month">Last Month</option>
                <option value="last_year">Last Year</option>
                <option value="specific_month">Specific Month</option>
                <option value="custom_range">Custom Range</option>
                <option value="specific_date">Specific Date</option>
              </Select>
            </FormControl>
            {exportFilters.time_filter_type === "specific_date" && <Input type="date" value={exportFilters.date} onChange={handleExportChange("date")} />}
            {exportFilters.time_filter_type === "specific_month" && <Flex gap={4}><Input type="number" placeholder="YYYY" value={exportFilters.year} onChange={handleExportChange("year")} /><Input type="number" placeholder="MM" min={1} max={12} value={exportFilters.month} onChange={handleExportChange("month")} /></Flex>}
            {exportFilters.time_filter_type === "custom_range" && <Flex gap={4}><Input type="date" value={exportFilters.start_date} onChange={handleExportChange("start_date")} /><Input type="date" value={exportFilters.end_date} onChange={handleExportChange("end_date")} /></Flex>}
            <Select placeholder="Sentiment" value={exportFilters.sentiment} onChange={handleExportChange("sentiment")}>
              <option value="bad">Bad</option>
              <option value="good">Good</option>
            </Select>
            <Select placeholder="Priority" value={exportFilters.priority} onChange={handleExportChange("priority")}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </Select>
            <Input placeholder="User ID" value={exportFilters.user_id} onChange={handleExportChange("user_id")} />
            <Button colorScheme="blue" onClick={handleExport} isLoading={loadingExport}>
              Export CSV
            </Button>
          </Stack>
        </Box>
      </Card>

      {/* Export Chat Data */}
      <Card mb={8}>
        <Box px={6} py={4}>
          <Text fontSize="xl" fontWeight="bold" mb={4}>Export Chat Data</Text>
          <Stack spacing={4}>
            <FormControl>
              <FormLabel>Time Filter Type</FormLabel>
              <Select
                placeholder="Select filter"
                value={chatExportFilters.time_filter_type}
                onChange={handleChatExportChange("time_filter_type")}
              >
                <option value="last_day">Last Day</option>
                <option value="last_week">Last Week</option>
                <option value="last_month">Last Month</option>
                <option value="last_year">Last Year</option>
                <option value="specific_month">Specific Month</option>
                <option value="custom_range">Custom Range</option>
                <option value="specific_date">Specific Date</option>
              </Select>
            </FormControl>
            {chatExportFilters.time_filter_type === "specific_date" && (
              <Input
                type="date"
                value={chatExportFilters.date_}
                onChange={handleChatExportChange("date_")}
              />
            )}
            {chatExportFilters.time_filter_type === "specific_month" && (
              <Flex gap={4}>
                <Input
                  type="number"
                  placeholder="YYYY"
                  value={chatExportFilters.year}
                  onChange={handleChatExportChange("year")}
                />
                <Input
                  type="number"
                  placeholder="MM"
                  min={1}
                  max={12}
                  value={chatExportFilters.month}
                  onChange={handleChatExportChange("month")}
                />
              </Flex>
            )}
            {chatExportFilters.time_filter_type === "custom_range" && (
              <Flex gap={4}>
                <Input
                  type="date"
                  value={chatExportFilters.start_date}
                  onChange={handleChatExportChange("start_date")}
                />
                <Input
                  type="date"
                  value={chatExportFilters.end_date}
                  onChange={handleChatExportChange("end_date")}
                />
              </Flex>
            )}
            <Input
              placeholder="User IDs (comma-separated)"
              value={chatExportFilters.user_ids}
              onChange={handleChatExportChange("user_ids")}
            />
            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="include-messages" mb="0">
                Include Messages
              </FormLabel>
              <Switch
                id="include-messages"
                isChecked={chatExportFilters.include_messages}
                onChange={handleChatExportChange("include_messages")}
              />
            </FormControl>
            <Button
              colorScheme="blue"
              onClick={handleChatExport}
              isLoading={loadingChatExport}
            >
              Export CSV
            </Button>
          </Stack>
        </Box>
      </Card>

      {/* Feedback Delete */}
      <Card mb={8}>
        <Box px={6} py={4}>
          <Text fontSize="xl" fontWeight="bold" mb={4}>Batch Delete Feedbacks</Text>
          <Stack spacing={4}>
            <FormControl>
              <FormLabel>Time Filter Type</FormLabel>
              <Select placeholder="Select" value={fbFilters.time_filter_type} onChange={handleFbChange("time_filter_type")}>                
                <option value="specific_date">Specific Date</option>
                <option value="specific_month">Specific Month</option>
                <option value="custom_range">Custom Range</option>
              </Select>
            </FormControl>
            {fbFilters.time_filter_type === "specific_date" && <Input type="date" value={fbFilters.date} onChange={handleFbChange("date")} />}
            {fbFilters.time_filter_type === "specific_month" && <Flex gap={4}><Input type="number" placeholder="YYYY" value={fbFilters.year} onChange={handleFbChange("year")} /><Input type="number" placeholder="MM" min={1} max={12} value={fbFilters.month} onChange={handleFbChange("month")} /></Flex>}
            {fbFilters.time_filter_type === "custom_range" && <Flex gap={4}><Input type="date" value={fbFilters.start_date} onChange={handleFbChange("start_date")} /><Input type="date" value={fbFilters.end_date} onChange={handleFbChange("end_date")} /></Flex>}
            <Select placeholder="Sentiment" value={fbFilters.sentiment} onChange={handleFbChange("sentiment")}>                
                <option value="positive">Positive</option>
                <option value="neutral">Neutral</option>
                <option value="negative">Negative</option>
            </Select>
            <Select placeholder="Priority" value={fbFilters.priority} onChange={handleFbChange("priority")}>                
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
            </Select>
            <Input placeholder="User ID" value={fbFilters.user_id} onChange={handleFbChange("user_id")} />
            <Input placeholder="Feedback IDs (comma-separated)" value={fbFilters.feedback_ids} onChange={handleFbChange("feedback_ids")} />
            <Button colorScheme="red" onClick={handleFbDelete} isLoading={loadingFbDelete}>Delete Feedbacks</Button>
          </Stack>
        </Box>
      </Card>

      {/* Chat Delete */}
      <Card>
        <Box px={6} py={4}>
          <Text fontSize="xl" fontWeight="bold" mb={4}>Batch Delete Chats</Text>
          <Stack spacing={4}>
            <FormControl>
              <FormLabel>Time Filter Type</FormLabel>
              <Select placeholder="Select" value={chatFilters.time_filter_type} onChange={handleChatChange("time_filter_type")}>
                <option value="specific_date">Specific Date</option>
                <option value="specific_month">Specific Month</option>
                <option value="custom_range">Custom Range</option>
              </Select>
            </FormControl>
            {chatFilters.time_filter_type === "specific_date" && <Input type="date" value={chatFilters.date} onChange={handleChatChange("date")} />}
            {chatFilters.time_filter_type === "specific_month" && <Flex gap={4}><Input type="number" placeholder="YYYY" value={chatFilters.year} onChange={handleChatChange("year")} /><Input type="number" placeholder="MM" min={1} max={12} value={chatFilters.month} onChange={handleChatChange("month")} /></Flex>}
            {chatFilters.time_filter_type === "custom_range" && <Flex gap={4}><Input type="date" value={chatFilters.start_date} onChange={handleChatChange("start_date")} /><Input type="date" value={chatFilters.end_date} onChange={handleChatChange("end_date")} /></Flex>}
            <Input placeholder="User IDs (comma-separated)" value={chatFilters.user_ids} onChange={handleChatChange("user_ids")} />
            <Input placeholder="Chat IDs (comma-separated)" value={chatFilters.chat_ids} onChange={handleChatChange("chat_ids")} />
            <Button colorScheme="red" onClick={handleChatDelete} isLoading={loadingChatDelete}>Delete Chats</Button>
          </Stack>
        </Box>
      </Card>
    </Box>
  );
};
